<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { useCommissionAgreements } from '../composables/useCommissionAgreements';

const props = defineProps({
  agents: { type: Array, default: () => [] },
  canManage: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
  teams: { type: Array, default: () => [] },
});

const emit = defineEmits(['close']);

const TEXT = Object.freeze({
  active: 'Ativos',
  agreement: 'Acordo',
  agreements: 'Acordos de comissão',
  amount: 'Valor fixo',
  back: 'Voltar para acordos',
  beneficiary: 'Beneficiário',
  buyer: 'Compra',
  cancel: 'Cancelar',
  close: 'Fechar',
  commission: 'Comissão',
  create: 'Novo acordo',
  createAgreement: 'Criar acordo',
  createDescription: 'O rascunho só será ativado após a publicação autorizada.',
  currency: 'Moeda',
  detail: 'Detalhes do acordo',
  draft: 'Rascunhos',
  emptyAgreements: 'Nenhum acordo encontrado para os filtros atuais.',
  emptyLedger: 'Nenhum lançamento do livro-razão foi encontrado.',
  fixed: 'Valor fixo',
  imovelListing: 'ID do anúncio',
  ledger: 'Livro-razão de comissões',
  ledgerTotal: 'Lançamentos',
  list: 'Lista de acordos',
  loading: 'Carregando acordos e lançamentos...',
  notAvailable: '—',
  paymentEvent: 'Evento de pagamento',
  percentage: 'Percentual',
  publish: 'Publicar acordo',
  publishing: 'Publicando...',
  recordedChange: 'Alteração registrada',
  refresh: 'Atualizar',
  retry: 'Tentar novamente',
  seller: 'Venda',
  side: 'Lado da operação',
  status: 'Status',
  term: 'Condição comercial',
  unavailable: 'Acordos de comissão indisponíveis',
  unavailableDescription:
    'A configuração ou a permissão desta conta não permite acessar este recurso.',
  versionHistory: 'Histórico versionado',
  viewHistory: 'Ver histórico',
  wholeAgency: 'Imobiliária',
});

const STATUS = Object.freeze({
  active: { label: 'Ativo', tone: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelado', tone: 'bg-slate-100 text-slate-600' },
  draft: { label: 'Rascunho', tone: 'bg-violet-100 text-lais-primary' },
  settled: { label: 'Liquidado', tone: 'bg-sky-100 text-sky-700' },
  superseded: { label: 'Substituído', tone: 'bg-amber-100 text-amber-700' },
});

const SIDES = Object.freeze([
  { value: 'seller', label: TEXT.seller },
  { value: 'buyer', label: TEXT.buyer },
  { value: 'both', label: 'Ambos' },
]);

const BENEFICIARIES = Object.freeze([
  { value: 'corretor', label: 'Corretor' },
  { value: 'equipe', label: 'Equipe' },
  { value: 'imobiliaria', label: TEXT.wholeAgency },
]);

const PAYMENT_EVENTS = Object.freeze([
  { value: 'on_signing', label: 'Na assinatura' },
  { value: 'on_deed', label: 'Na escritura' },
  { value: 'on_first_rent', label: 'No primeiro aluguel' },
  { value: 'on_closing', label: 'No fechamento' },
  { value: 'on_installments', label: 'Nas parcelas' },
  { value: 'custom', label: 'Evento personalizado' },
]);

const emptyForm = () => ({
  beneficiary_kind: 'corretor',
  beneficiary_team_id: '',
  beneficiary_user_id: '',
  commission_amount: '',
  commission_kind: 'percentage',
  commission_percentage: '',
  currency: 'BRL',
  imovel_listing_id: '',
  payment_event: 'on_closing',
  side: 'seller',
});

const state = useCommissionAgreements();
const currentView = ref('list');
const filterStatus = ref('');
const form = ref(emptyForm());

const agreementCount = computed(() => state.totalAgreements.value);
const ledgerCount = computed(() => state.totalLedger.value);
const selectedAgreement = computed(() => state.selectedAgreement.value);
const versions = computed(() => selectedAgreement.value?.versions || []);
const canManageAgreements = computed(
  () => props.canManage && !state.unavailable.value
);
const canCreate = computed(() => {
  const hasListing = Number(form.value.imovel_listing_id) > 0;
  const hasBeneficiary =
    form.value.beneficiary_kind === 'imobiliaria' ||
    (form.value.beneficiary_kind === 'corretor' &&
      Number(form.value.beneficiary_user_id) > 0) ||
    (form.value.beneficiary_kind === 'equipe' &&
      Number(form.value.beneficiary_team_id) > 0);
  const hasTerm =
    (form.value.commission_kind === 'percentage' &&
      form.value.commission_percentage !== '' &&
      Number(form.value.commission_percentage) >= 0) ||
    (form.value.commission_kind === 'fixed' &&
      form.value.commission_amount !== '' &&
      Number(form.value.commission_amount) >= 0);

  return hasListing && hasBeneficiary && hasTerm;
});

const statusMeta = status =>
  STATUS[status] || {
    label: status || TEXT.status,
    tone: 'bg-slate-100 text-slate-600',
  };

const sideLabel = side =>
  SIDES.find(option => option.value === side)?.label ||
  side ||
  TEXT.notAvailable;

const paymentEventLabel = event =>
  PAYMENT_EVENTS.find(option => option.value === event)?.label ||
  event ||
  TEXT.notAvailable;

const agentName = id =>
  props.agents.find(agent => String(agent.id) === String(id))?.name ||
  props.agents.find(agent => String(agent.id) === String(id))?.email ||
  `Corretor #${id}`;

const teamName = id =>
  props.teams.find(team => String(team.id) === String(id))?.name ||
  `Equipe #${id}`;

const beneficiaryLabel = agreement => {
  if (agreement?.beneficiary_kind === 'corretor') {
    return agentName(agreement.beneficiary_user_id);
  }
  if (agreement?.beneficiary_kind === 'equipe') {
    return teamName(agreement.beneficiary_team_id);
  }

  return TEXT.wholeAgency;
};

const moneyAmount = value => {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    if (value.amount != null) return Number(value.amount);
    if (value.value != null) return Number(value.value);
    if (value.cents != null) return Number(value.cents) / 100;
  }
  return Number(value);
};

const formatMoney = (value, currency = 'BRL') => {
  const amount = moneyAmount(value);
  if (!Number.isFinite(amount)) return '—';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency || 'BRL',
  }).format(amount);
};

const formatDate = value => {
  if (!value) return TEXT.notAvailable;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('pt-BR');
};

const formatTerm = agreement => {
  if (agreement?.commission_kind === 'fixed') {
    return formatMoney(agreement.commission_amount, agreement.currency);
  }

  return agreement?.commission_percentage != null
    ? `${agreement.commission_percentage}%`
    : TEXT.notAvailable;
};

const agreementTitle = id => `${TEXT.agreement} #${id}`;

const agreementSummary = agreement =>
  `${TEXT.imovelListing} #${agreement.imovel_listing_id} · ${sideLabel(
    agreement.side
  )} · ${beneficiaryLabel(agreement)}`;

const agreementTermSummary = agreement =>
  `${formatTerm(agreement)} · ${paymentEventLabel(agreement.payment_event)}`;

const agreementDetailMeta = agreement =>
  `${TEXT.imovelListing} #${agreement.imovel_listing_id} · ${paymentEventLabel(
    agreement.payment_event
  )}`;

const versionTitle = version =>
  `${TEXT.agreement} · v${version.version_number}`;

const versionMeta = version =>
  `${version.change_kind || TEXT.recordedChange} · ${formatDate(
    version.recorded_at
  )}`;

const ledgerTitle = entry =>
  `${entry.source_type || TEXT.ledger} #${entry.source_id || entry.id}`;

const ledgerMeta = entry =>
  `${entry.business_context || TEXT.notAvailable} · ${formatDate(
    entry.earned_at || entry.created_at
  )}`;

const close = () => emit('close');

const load = () =>
  state.load(filterStatus.value ? { status: filterStatus.value } : {});

const openList = () => {
  currentView.value = 'list';
  state.selectedAgreement.value = null;
  state.actionError.value = '';
};

const openCreate = () => {
  if (!canManageAgreements.value) return;

  currentView.value = 'create';
  state.actionError.value = '';
};

const openDetail = async agreement => {
  currentView.value = 'detail';
  await state.open(agreement.id);
};

const publish = async () => {
  if (!canManageAgreements.value || !selectedAgreement.value?.id) return;
  await state.publish(selectedAgreement.value.id);
};

const submitCreate = async () => {
  if (!canManageAgreements.value || !canCreate.value) return;

  const payload = {
    beneficiary_kind: form.value.beneficiary_kind,
    commission_kind: form.value.commission_kind,
    currency: form.value.currency,
    imovel_listing_id: Number(form.value.imovel_listing_id),
    payment_event: form.value.payment_event,
    side: form.value.side,
  };

  if (form.value.beneficiary_kind === 'corretor') {
    payload.beneficiary_user_id = Number(form.value.beneficiary_user_id);
  }
  if (form.value.beneficiary_kind === 'equipe') {
    payload.beneficiary_team_id = Number(form.value.beneficiary_team_id);
  }
  if (form.value.commission_kind === 'percentage') {
    payload.commission_percentage = form.value.commission_percentage;
  } else {
    payload.commission_amount = form.value.commission_amount;
  }

  const agreement = await state.create(payload);
  if (!agreement) return;

  form.value = emptyForm();
  currentView.value = 'detail';
};

watch(
  () => props.show,
  open => {
    if (open) load();
  }
);

watch(filterStatus, () => {
  if (props.show) load();
});
</script>

<template>
  <LaisModal
    :show="show"
    :title="currentView === 'create' ? TEXT.createAgreement : TEXT.agreements"
    max-width-class="max-w-[1180px]"
    @close="close"
  >
    <div v-if="state.loading.value" class="grid gap-3">
      <div
        v-for="index in 4"
        :key="index"
        class="h-20 animate-pulse rounded-lais bg-lais-bg-soft"
      />
      <p class="text-sm text-lais-text-muted">{{ TEXT.loading }}</p>
    </div>

    <div v-else-if="state.unavailable.value" class="grid gap-3">
      <div
        class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-4"
      >
        <div class="flex items-start gap-3">
          <span
            class="grid size-9 shrink-0 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
          >
            <i class="i-lucide-lock-keyhole size-4" />
          </span>
          <div class="min-w-0">
            <h3 class="text-sm font-semibold text-lais-text">
              {{ TEXT.unavailable }}
            </h3>
            <p class="mt-1 text-sm text-lais-text-muted">
              {{ TEXT.unavailableDescription }}
            </p>
            <p class="mt-2 text-xs text-lais-text-helper">
              {{ state.unavailableMessage.value }}
            </p>
          </div>
        </div>
      </div>
      <div>
        <LaisPrimaryButton size="sm" variant="ghost" @click="load">
          {{ TEXT.retry }}
        </LaisPrimaryButton>
      </div>
    </div>

    <div v-else-if="state.error.value" class="grid gap-3">
      <p
        class="rounded-lais-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
      >
        {{ state.error.value }}
      </p>
      <div>
        <LaisPrimaryButton size="sm" @click="load">
          {{ TEXT.retry }}
        </LaisPrimaryButton>
      </div>
    </div>

    <section v-else-if="currentView === 'create'" class="grid gap-5">
      <div>
        <h3 class="text-lais-heading-sm font-semibold text-lais-text">
          {{ TEXT.createAgreement }}
        </h3>
        <p class="mt-1 text-sm text-lais-text-muted">
          {{ TEXT.createDescription }}
        </p>
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <label class="grid gap-1.5 text-sm font-semibold text-lais-text">
          {{ TEXT.imovelListing }}
          <input
            v-model="form.imovel_listing_id"
            type="number"
            min="1"
            inputmode="numeric"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-sm font-normal"
          />
        </label>
        <label class="grid gap-1.5 text-sm font-semibold text-lais-text">
          {{ TEXT.side }}
          <select
            v-model="form.side"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-sm font-normal"
          >
            <option
              v-for="option in SIDES"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="grid gap-1.5 text-sm font-semibold text-lais-text">
          {{ TEXT.beneficiary }}
          <select
            v-model="form.beneficiary_kind"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-sm font-normal"
          >
            <option
              v-for="option in BENEFICIARIES"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label
          v-if="form.beneficiary_kind === 'corretor'"
          class="grid gap-1.5 text-sm font-semibold text-lais-text"
        >
          {{ BENEFICIARIES[0].label }}
          <select
            v-model="form.beneficiary_user_id"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-sm font-normal"
          >
            <option value="">{{ TEXT.notAvailable }}</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name || agent.email }}
            </option>
          </select>
        </label>
        <label
          v-else-if="form.beneficiary_kind === 'equipe'"
          class="grid gap-1.5 text-sm font-semibold text-lais-text"
        >
          {{ BENEFICIARIES[1].label }}
          <select
            v-model="form.beneficiary_team_id"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-sm font-normal"
          >
            <option value="">{{ TEXT.notAvailable }}</option>
            <option v-for="team in teams" :key="team.id" :value="team.id">
              {{ team.name }}
            </option>
          </select>
        </label>
        <div v-else class="hidden sm:block" />
        <label class="grid gap-1.5 text-sm font-semibold text-lais-text">
          {{ TEXT.commission }}
          <select
            v-model="form.commission_kind"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-sm font-normal"
          >
            <option value="percentage">{{ TEXT.percentage }}</option>
            <option value="fixed">{{ TEXT.fixed }}</option>
          </select>
        </label>
        <label
          v-if="form.commission_kind === 'percentage'"
          class="grid gap-1.5 text-sm font-semibold text-lais-text"
        >
          {{ TEXT.percentage }}
          <input
            v-model="form.commission_percentage"
            type="number"
            min="0"
            max="100"
            step="0.0001"
            inputmode="decimal"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-sm font-normal"
          />
        </label>
        <label v-else class="grid gap-1.5 text-sm font-semibold text-lais-text">
          {{ TEXT.amount }}
          <input
            v-model="form.commission_amount"
            type="number"
            min="0"
            step="0.01"
            inputmode="decimal"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-sm font-normal"
          />
        </label>
        <label class="grid gap-1.5 text-sm font-semibold text-lais-text">
          {{ TEXT.paymentEvent }}
          <select
            v-model="form.payment_event"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-sm font-normal"
          >
            <option
              v-for="option in PAYMENT_EVENTS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="grid gap-1.5 text-sm font-semibold text-lais-text">
          {{ TEXT.currency }}
          <input
            v-model="form.currency"
            maxlength="3"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-sm font-normal uppercase"
          />
        </label>
      </div>

      <p
        v-if="state.actionError.value"
        class="rounded-lais-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
      >
        {{ state.actionError.value }}
      </p>
    </section>

    <section v-else-if="currentView === 'detail'" class="grid gap-5">
      <div v-if="state.detailLoading.value" class="grid gap-3">
        <div class="h-28 animate-pulse rounded-lais bg-lais-bg-soft" />
        <div class="h-40 animate-pulse rounded-lais bg-lais-bg-soft" />
      </div>

      <template v-else-if="selectedAgreement">
        <LaisCard :title="`${TEXT.agreement} #${selectedAgreement.id}`">
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p class="text-xs text-lais-text-muted">{{ TEXT.status }}</p>
              <span
                class="mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold"
                :class="statusMeta(selectedAgreement.status).tone"
              >
                {{ statusMeta(selectedAgreement.status).label }}
              </span>
            </div>
            <div>
              <p class="text-xs text-lais-text-muted">{{ TEXT.side }}</p>
              <p class="mt-1 text-sm font-semibold text-lais-text">
                {{ sideLabel(selectedAgreement.side) }}
              </p>
            </div>
            <div>
              <p class="text-xs text-lais-text-muted">{{ TEXT.beneficiary }}</p>
              <p class="mt-1 text-sm font-semibold text-lais-text">
                {{ beneficiaryLabel(selectedAgreement) }}
              </p>
            </div>
            <div>
              <p class="text-xs text-lais-text-muted">{{ TEXT.term }}</p>
              <p class="mt-1 text-sm font-semibold text-lais-text">
                {{ formatTerm(selectedAgreement) }}
              </p>
            </div>
          </div>
          <div
            class="mt-4 border-t border-lais-border pt-4 text-sm text-lais-text-muted"
          >
            {{ agreementDetailMeta(selectedAgreement) }}
          </div>
        </LaisCard>

        <LaisCard :title="TEXT.versionHistory" prefix-icon="i-lucide-history">
          <div v-if="versions.length" class="grid gap-3">
            <article
              v-for="version in versions"
              :key="version.version_number"
              class="grid gap-2 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3 sm:grid-cols-[minmax(0,1fr)_auto]"
            >
              <div>
                <p class="text-sm font-semibold text-lais-text">
                  {{ versionTitle(version) }}
                </p>
                <p class="mt-1 text-xs text-lais-text-muted">
                  {{ versionMeta(version) }}
                </p>
                <p
                  v-if="version.reason"
                  class="mt-1 text-xs text-lais-text-muted"
                >
                  {{ version.reason }}
                </p>
              </div>
              <div class="text-right text-xs text-lais-text-muted">
                <p>
                  {{
                    formatMoney(
                      version.computed_amount,
                      selectedAgreement.currency
                    )
                  }}
                </p>
                <p class="mt-1">{{ statusMeta(version.status).label }}</p>
              </div>
            </article>
          </div>
          <p v-else class="text-sm text-lais-text-muted">
            {{ TEXT.emptyAgreements }}
          </p>
        </LaisCard>

        <p
          v-if="state.actionError.value"
          class="rounded-lais-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {{ state.actionError.value }}
        </p>
      </template>
    </section>

    <section v-else class="grid gap-5">
      <div class="grid gap-3 sm:grid-cols-3">
        <LaisCard :title="TEXT.agreements" prefix-icon="i-lucide-file-text">
          <p class="text-2xl font-bold text-lais-text">{{ agreementCount }}</p>
          <p class="mt-1 text-sm text-lais-text-muted">{{ TEXT.list }}</p>
        </LaisCard>
        <LaisCard :title="TEXT.active" prefix-icon="i-lucide-circle-check-big">
          <p class="text-2xl font-bold text-emerald-700">
            {{
              state.agreements.value.filter(item => item.status === 'active')
                .length
            }}
          </p>
          <p class="mt-1 text-sm text-lais-text-muted">{{ TEXT.active }}</p>
        </LaisCard>
        <LaisCard :title="TEXT.ledger" prefix-icon="i-lucide-book-open-check">
          <p class="text-2xl font-bold text-lais-text">{{ ledgerCount }}</p>
          <p class="mt-1 text-sm text-lais-text-muted">
            {{ TEXT.ledgerTotal }}
          </p>
        </LaisCard>
      </div>

      <div class="flex flex-wrap items-end justify-between gap-3">
        <label class="grid gap-1 text-sm font-semibold text-lais-text">
          {{ TEXT.status }}
          <select
            v-model="filterStatus"
            class="min-w-44 rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-sm font-normal"
          >
            <option value="">{{ TEXT.status }}</option>
            <option v-for="(meta, key) in STATUS" :key="key" :value="key">
              {{ meta.label }}
            </option>
          </select>
        </label>
        <div class="flex flex-wrap gap-2">
          <LaisPrimaryButton
            size="sm"
            variant="ghost"
            icon="i-lucide-refresh-cw"
            @click="load"
          >
            {{ TEXT.refresh }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            v-if="canManageAgreements"
            size="sm"
            icon="i-lucide-plus"
            @click="openCreate"
          >
            {{ TEXT.create }}
          </LaisPrimaryButton>
        </div>
      </div>

      <div
        class="grid gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]"
      >
        <LaisCard
          :title="TEXT.agreements"
          prefix-icon="i-lucide-file-signature"
        >
          <div v-if="state.agreements.value.length" class="grid gap-2">
            <article
              v-for="agreement in state.agreements.value"
              :key="agreement.id"
              class="grid gap-3 rounded-lais-sm border border-lais-border-soft px-3 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
            >
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <p class="text-sm font-semibold text-lais-text">
                    {{ agreementTitle(agreement.id) }}
                  </p>
                  <span
                    class="rounded-full px-2 py-0.5 text-xs font-semibold"
                    :class="statusMeta(agreement.status).tone"
                  >
                    {{ statusMeta(agreement.status).label }}
                  </span>
                </div>
                <p class="mt-1 text-xs text-lais-text-muted">
                  {{ agreementSummary(agreement) }}
                </p>
                <p class="mt-1 text-xs text-lais-text-muted">
                  {{ agreementTermSummary(agreement) }}
                </p>
              </div>
              <LaisPrimaryButton
                size="sm"
                variant="ghost"
                @click="openDetail(agreement)"
              >
                {{ TEXT.viewHistory }}
              </LaisPrimaryButton>
            </article>
          </div>
          <p v-else class="text-sm text-lais-text-muted">
            {{ TEXT.emptyAgreements }}
          </p>
        </LaisCard>

        <LaisCard :title="TEXT.ledger" prefix-icon="i-lucide-book-open-check">
          <div v-if="state.ledger.value.length" class="grid gap-3">
            <article
              v-for="entry in state.ledger.value.slice(0, 6)"
              :key="entry.id"
              class="border-b border-lais-border-soft pb-3 last:border-b-0 last:pb-0"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-lais-text">
                    {{ ledgerTitle(entry) }}
                  </p>
                  <p class="mt-1 text-xs text-lais-text-muted">
                    {{ ledgerMeta(entry) }}
                  </p>
                </div>
                <span
                  class="shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold"
                  :class="statusMeta(entry.status).tone"
                >
                  {{ statusMeta(entry.status).label }}
                </span>
              </div>
              <p class="mt-2 text-sm font-semibold text-lais-text">
                {{ formatMoney(entry.value) }}
              </p>
            </article>
          </div>
          <p v-else class="text-sm text-lais-text-muted">
            {{ TEXT.emptyLedger }}
          </p>
        </LaisCard>
      </div>
    </section>

    <template #footer>
      <div class="flex flex-wrap items-center justify-between gap-3">
        <LaisPrimaryButton
          v-if="currentView !== 'list'"
          variant="ghost"
          :disabled="state.saving.value"
          @click="openList"
        >
          {{ TEXT.back }}
        </LaisPrimaryButton>
        <span v-else />
        <div class="flex flex-wrap gap-2">
          <LaisPrimaryButton
            v-if="
              currentView === 'detail' &&
              selectedAgreement?.status === 'draft' &&
              canManageAgreements
            "
            :is-loading="state.saving.value"
            @click="publish"
          >
            {{ TEXT.publish }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            v-if="currentView === 'create' && canManageAgreements"
            :disabled="!canCreate"
            :is-loading="state.saving.value"
            @click="submitCreate"
          >
            {{ TEXT.createAgreement }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="ghost"
            :disabled="state.saving.value"
            @click="close"
          >
            {{ currentView === 'create' ? TEXT.cancel : TEXT.close }}
          </LaisPrimaryButton>
        </div>
      </div>
    </template>
  </LaisModal>
</template>
